import { useState } from 'react';
import { api, errorMessage } from '../api/client';
import type { AdminUser, UserRole, UserStatus } from '../api/types';
import { useAuth } from '../api/auth';
import { useResource } from '../api/useResource';
import { Panel, Select, Spinner, Status, TextInput } from '../components/ui';

export function UsersPage() {
  const { admin } = useAuth();
  const [query, setQuery] = useState('');
  const [applied, setApplied] = useState('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const users = useResource(
    () =>
      api<{ users: AdminUser[]; nextCursor: string | null }>(
        `/v1/admin/users?${new URLSearchParams({
          ...(applied ? { q: applied } : {}),
          ...(status ? { status } : {}),
        }).toString()}`,
      ),
    [applied, status],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function patch(user: AdminUser, body: { status?: UserStatus; role?: UserRole }) {
    setBusyId(user.id);
    setError(null);
    setMessage(null);

    try {
      await api(`/v1/admin/users/${user.id}`, { method: 'PATCH', body });
      setMessage(`Updated ${user.email}.`);
      await users.reload();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusyId(null);
    }
  }

  if (users.loading && !users.data) return <Spinner />;

  return (
    <Panel title="Users" actions={<span className="muted">{users.data?.users.length ?? 0} shown</span>}>
      <p className="muted">
        Suspending an account revokes its refresh tokens on the spot, so the player is signed out
        of every device rather than lasting another access-token lifetime. You cannot suspend or
        demote your own admin account.
      </p>

      <div className="row">
        <TextInput value={query} onChange={setQuery} placeholder="Search email or display name" />
        <button className="button button-ghost" onClick={() => setApplied(query.trim())}>
          Search
        </button>
        <Select
          value={status}
          options={[
            { value: '' as UserStatus | '', label: 'Any status' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'SUSPENDED', label: 'Suspended' },
            { value: 'PENDING_DELETION', label: 'Pending deletion' },
          ]}
          onChange={setStatus}
        />
      </div>

      <Status error={users.error} />

      <table className="table">
        <thead>
          <tr>
            <th />
            <th>Email</th>
            <th>Display name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Joined</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.data?.users.map((user) => {
            const self = user.id === admin?.id;

            return (
              <tr key={user.id} className={user.status === 'ACTIVE' ? undefined : 'row-inactive'}>
                <td>
                  {user.avatarUrl ? (
                    <img className="avatar" src={user.avatarUrl} alt="" />
                  ) : (
                    <span className="avatar-placeholder">
                      {user.displayName.trim().charAt(0) || '?'}
                    </span>
                  )}
                </td>
                <td>{user.email}</td>
                <td>{user.displayName}</td>
                <td>{user.role}</td>
                <td>{user.status}</td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td className="row">
                  <button
                    className="button button-ghost"
                    disabled={self || busyId === user.id}
                    onClick={() =>
                      void patch(user, {
                        status: user.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED',
                      })
                    }
                  >
                    {user.status === 'SUSPENDED' ? 'Reactivate' : 'Suspend'}
                  </button>
                  <button
                    className="button button-ghost"
                    disabled={self || busyId === user.id}
                    onClick={() =>
                      void patch(user, { role: user.role === 'ADMIN' ? 'PLAYER' : 'ADMIN' })
                    }
                  >
                    {user.role === 'ADMIN' ? 'Demote' : 'Make admin'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Status error={error} message={message} />
    </Panel>
  );
}
